import { Children, isValidElement, type ReactNode } from 'react';
import { cn } from '@/utils';
import { type AiBuilderJumpLink } from '../store';
import { type AiBuilderResponseFeedbackTargetMessage } from '../utils/aiBuilderResponseFeedback';
import { AiBuilderAssistantMessageFrame } from './AiBuilderAssistantMessageFrame';
import { AiBuilderAssistantMessageSlot } from './AiBuilderAssistantMessageSlot';
import { AiBuilderJumpLinkList } from './AiBuilderJumpLinkList';
import { AiBuilderMessageCopyButton } from './AiBuilderMessageCopyButton';
import { type AiBuilderProgressMessage, AiBuilderProgressMessageContent, type AiBuilderProgressMessageStatus } from './AiBuilderProgressMessageContent';
import { AiBuilderResponseFeedbackControls } from './AiBuilderResponseFeedbackControls';
type AiBuilderAssistantMessageVisualState = 'thinking' | 'writing' | 'default' | 'error';
type AiBuilderChatMessageAIProps = {
  tone?: 'error';
  children: ReactNode;
};
type ProgressProps = {
  message: AiBuilderProgressMessage;
};
type BodyProps = {
  className?: string;
  children: ReactNode;
};
type JumpLinksProps = {
  links: AiBuilderJumpLink[];
  onLinkClick?: (quizNumber: number) => void;
};
type CopyProps = {
  text: string;
  feedbackMessage?: AiBuilderResponseFeedbackTargetMessage;
};
const Progress = ({
  message
}: ProgressProps) => {
  return <AiBuilderProgressMessageContent message={message} data-sentry-element="AiBuilderProgressMessageContent" data-sentry-component="Progress" data-sentry-source-file="AiBuilderChatMessageAI.tsx" />;
};
const Body = ({
  className,
  children
}: BodyProps) => {
  return <AiBuilderAssistantMessageSlot className={className} variant="body" data-ai-assistant-body-slot data-sentry-element="AiBuilderAssistantMessageSlot" data-sentry-component="Body" data-sentry-source-file="AiBuilderChatMessageAI.tsx">
      {children}
    </AiBuilderAssistantMessageSlot>;
};
const JumpLinks = ({
  links,
  onLinkClick
}: JumpLinksProps) => {
  return <AiBuilderAssistantMessageSlot variant="jumpLinks" data-ai-assistant-jump-link-slot data-sentry-element="AiBuilderAssistantMessageSlot" data-sentry-component="JumpLinks" data-sentry-source-file="AiBuilderChatMessageAI.tsx">
      <AiBuilderJumpLinkList jumpLinks={links} onJumpLinkClick={onLinkClick} data-sentry-element="AiBuilderJumpLinkList" data-sentry-source-file="AiBuilderChatMessageAI.tsx" />
    </AiBuilderAssistantMessageSlot>;
};
const Copy = ({
  feedbackMessage,
  text
}: CopyProps) => {
  return <AiBuilderAssistantMessageSlot variant="action" data-ai-assistant-action-row data-sentry-element="AiBuilderAssistantMessageSlot" data-sentry-component="Copy" data-sentry-source-file="AiBuilderChatMessageAI.tsx">
      <div className="flex w-full items-center gap-4xs">
        <AiBuilderMessageCopyButton className="w-auto shrink-0" text={text} data-sentry-element="AiBuilderMessageCopyButton" data-sentry-source-file="AiBuilderChatMessageAI.tsx" />
        {feedbackMessage?.traceId && <AiBuilderResponseFeedbackControls message={feedbackMessage} />}
      </div>
    </AiBuilderAssistantMessageSlot>;
};
const deriveAssistantMessageVisualStateFromSlots = (children: ReactNode, tone?: 'error'): AiBuilderAssistantMessageVisualState => {
  let hasBody = false;
  let progressStatus: AiBuilderProgressMessageStatus | undefined;
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if (child.type === Body) {
      hasBody = true;
    } else if (child.type === Progress) {
      progressStatus = (child.props as ProgressProps).message.status;
    }
  }
  if (tone === 'error' || progressStatus === 'stopped') return 'error';
  if (progressStatus === 'running') return hasBody ? 'writing' : 'thinking';
  return 'default';
};
export const AiBuilderChatMessageAI = ({
  children,
  tone
}: AiBuilderChatMessageAIProps) => {
  const state = deriveAssistantMessageVisualStateFromSlots(children, tone);
  return <AiBuilderAssistantMessageFrame className={cn(state === 'error' && 'pt-none')} data-ai-chat-message-ai data-ai-chat-message-ai-state={state} data-sentry-element="AiBuilderAssistantMessageFrame" data-sentry-component="AiBuilderChatMessageAI" data-sentry-source-file="AiBuilderChatMessageAI.tsx">
      {children}
    </AiBuilderAssistantMessageFrame>;
};
AiBuilderChatMessageAI.Progress = Progress;
AiBuilderChatMessageAI.Body = Body;
AiBuilderChatMessageAI.JumpLinks = JumpLinks;
AiBuilderChatMessageAI.Copy = Copy;